/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { RefObject } from 'preact';
import { useRef } from 'preact/hooks';
import { useDrag } from './useDrag';
import { Range, pan } from '../utils/panUtils';
import { useUser } from '#hooks/UNSAFE_useUser';

type UseDragPanOptions = {
  captureTargetRef: RefObject<HTMLDivElement>;
  width: number;
  height: number;
  getRangeX: () => Range;
  getRangeY: () => Range;
  globalRangeX: Range;
  globalRangeY: Range;
  onRangeXChange: (detail: Range) => void;
  onRangeYChange: (detail: Range) => void;
  onDragPanStart?: () => void;
  onDragPanEnd?: () => void;
  isDisabled?: boolean;
};

/**
 * Hook that enables and handles drag pan gestures.
 */
const useDragPan = ({
  captureTargetRef,
  width,
  height,
  getRangeX,
  getRangeY,
  globalRangeX,
  globalRangeY,
  onRangeXChange,
  onRangeYChange,
  onDragPanStart,
  onDragPanEnd,
  isDisabled = false
}: UseDragPanOptions) => {
  const { direction } = useUser();
  const prevRangeX = useRef({ min: 0, max: 0 });
  const prevRangeY = useRef({ min: 0, max: 0 });
  const initialRangeX = useRef({ min: 0, max: 0 });
  const initialRangeY = useRef({ min: 0, max: 0 });
  const isPanning = useRef(false);

  const dragProps = useDrag({
    captureTargetRef,
    onDragStart: () => {
      initialRangeX.current = getRangeX();
      initialRangeY.current = getRangeY();
      onDragPanStart && onDragPanStart();
    },
    onDragMove: ({ dx, dy }) => {
      isPanning.current = true;

      const { newRange: newRangeX } = pan(
        ((direction === 'rtl' ? -1 : 1) * dx) / width,
        initialRangeX.current,
        prevRangeX.current,
        globalRangeX,
        onRangeXChange
      );
      prevRangeX.current = newRangeX;

      const { newRange: newRangeY } = pan(
        dy / height,
        initialRangeY.current,
        prevRangeY.current,
        globalRangeY,
        onRangeYChange
      );
      prevRangeY.current = newRangeY;
    },
    onDragEnd: ({ originalEvent }) => {
      // If panning occurred, call preventDefault to prevent selection from occurring
      if (isPanning.current) originalEvent.preventDefault();
      isPanning.current = false;
      onDragPanEnd && onDragPanEnd();
    },
    isDisabled
  });

  return isDisabled ? {} : dragProps;
};

export { useDragPan };
